import React, { useState, useEffect, useCallback } from 'react';
import { useT } from '../../context/TenantContext';
import BackDeskLayout from '../../components/layout/BackDeskLayout';
import { api } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui';

const DAYS_ES  = ['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'];
const MONTHS_ES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const COLORS = [
  { value:'#0085CA', label:'Azul'   },
  { value:'#15803D', label:'Verde'  },
  { value:'#B45309', label:'Naranja'},
  { value:'#B91C1C', label:'Rojo'   },
  { value:'#7C3AED', label:'Morado' },
];

function pad(n) { return String(n).padStart(2,'0'); }
function toLocalISO(date, time) { return `${date}T${time}:00`; }
function isoToDateInput(iso) {
  if (!iso) return '';
  const s = iso.replace('Z','').replace(' ','T');
  return s.slice(0,10);
}
function isoToTimeInput(iso) {
  if (!iso) return '09:00';
  const s = iso.replace('Z','').replace(' ','T');
  return s.slice(11,16);
}

const EMPTY_FORM = { title:'', description:'', date:'', startTime:'09:00', endTime:'10:00', color:'#0085CA', all_day:false };

export default function Calendar() {
  const { t } = useT();
  const { agent } = useAuth();
  const today = new Date();
  const [year,  setYear]  = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth()); // 0-based
  const [events, setEvents] = useState([]);
  const [modal, setModal] = useState(null); // null | { mode:'create'|'edit', event?:{}, date?:string }
  const [form,  setForm]  = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [delConfirm, setDelConfirm] = useState(false);

  const loadEvents = useCallback(async () => {
    const from = `${year}-${pad(month+1)}-01`;
    const lastDay = new Date(year, month+1, 0).getDate();
    const to = `${year}-${pad(month+1)}-${pad(lastDay)}T23:59:59`;
    const data = await api.getEvents(from, to);
    setEvents(data || []);
  }, [year, month]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  function prevMonth() {
    if (month === 0) { setYear(y => y-1); setMonth(11); }
    else setMonth(m => m-1);
  }
  function nextMonth() {
    if (month === 11) { setYear(y => y+1); setMonth(0); }
    else setMonth(m => m+1);
  }
  function goToday() { setYear(today.getFullYear()); setMonth(today.getMonth()); }

  // Build grid: get first Monday on or before the 1st of the month
  function buildGrid() {
    const firstDay = new Date(year, month, 1);
    let startDow = firstDay.getDay(); // 0=Sun
    startDow = startDow === 0 ? 6 : startDow - 1; // shift to Mon=0
    const cells = [];
    const start = new Date(firstDay);
    start.setDate(start.getDate() - startDow);
    while (cells.length < 42) {
      cells.push(new Date(start));
      start.setDate(start.getDate() + 1);
    }
    return cells;
  }

  function eventsForDay(date) {
    const dayStr = `${date.getFullYear()}-${pad(date.getMonth()+1)}-${pad(date.getDate())}`;
    return events.filter(ev => {
      const evDate = (ev.start_at||'').slice(0,10);
      return evDate === dayStr;
    });
  }

  function openCreate(date) {
    const dateStr = `${date.getFullYear()}-${pad(date.getMonth()+1)}-${pad(date.getDate())}`;
    setForm({ ...EMPTY_FORM, date: dateStr });
    setDelConfirm(false);
    setModal({ mode:'create' });
  }

  function openEdit(ev, e) {
    e.stopPropagation();
    setForm({
      title:       ev.title,
      description: ev.description || '',
      date:        isoToDateInput(ev.start_at),
      startTime:   isoToTimeInput(ev.start_at),
      endTime:     isoToTimeInput(ev.end_at),
      color:       ev.color || '#0085CA',
      all_day:     !!ev.all_day,
    });
    setDelConfirm(false);
    setModal({ mode:'edit', event: ev });
  }

  function closeModal() { setModal(null); setForm(EMPTY_FORM); setDelConfirm(false); }

  async function handleSave() {
    if (!form.title.trim() || !form.date) return;
    setSaving(true);
    try {
      const body = {
        title:       form.title.trim(),
        description: form.description.trim(),
        start_at:    toLocalISO(form.date, form.startTime),
        end_at:      toLocalISO(form.date, form.endTime),
        color:       form.color,
        all_day:     form.all_day ? 1 : 0,
        ticket_id:   modal?.event?.ticket_id || null,
      };
      if (modal.mode === 'create') await api.createEvent(body);
      else await api.updateEvent(modal.event.id, body);
      closeModal();
      loadEvents();
    } finally { setSaving(false); }
  }

  async function handleDelete() {
    if (!delConfirm) { setDelConfirm(true); return; }
    setSaving(true);
    try {
      await api.deleteEvent(modal.event.id);
      closeModal();
      loadEvents();
    } finally { setSaving(false); }
  }

  const grid = buildGrid();
  const valid = form.title.trim() && form.date;

  const inputStyle = {
    width:'100%', height:36, padding:'0 10px', border:'1.5px solid #E5E7EB', borderRadius:8,
    fontSize:13, color:'#111827', outline:'none', background:'#fff', boxSizing:'border-box',
  };

  return (
    <BackDeskLayout title={t('nav.calendar')}>
      <div style={{display:'flex',flexDirection:'column',gap:16}}>

        {/* Header */}
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:12}}>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <Button variant="ghost" onClick={prevMonth} style={{width:32,height:32,padding:0,display:'flex',alignItems:'center',justifyContent:'center',fontSize:16}}>‹</Button>
            <span style={{fontSize:17,fontWeight:700,color:'#111827',minWidth:180,textAlign:'center'}}>
              {MONTHS_ES[month]} {year}
            </span>
            <Button variant="ghost" onClick={nextMonth} style={{width:32,height:32,padding:0,display:'flex',alignItems:'center',justifyContent:'center',fontSize:16}}>›</Button>
          </div>
          <Button variant="ghost" onClick={goToday}>{t('calendar.today')}</Button>
        </div>

        {/* Grid */}
        <div style={{background:'#fff',borderRadius:12,overflow:'hidden'}}>
          {/* Day headers */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',borderBottom:'1px solid #F3F4F6'}}>
            {DAYS_ES.map(d => (
              <div key={d} style={{padding:'10px 0',textAlign:'center',fontSize:11,fontWeight:700,
                color:'#9CA3AF',letterSpacing:.5,textTransform:'uppercase'}}>{d}</div>
            ))}
          </div>
          {/* Weeks */}
          {Array.from({length:6}, (_,wi) => (
            <div key={wi} style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)'}}>
              {grid.slice(wi*7, wi*7+7).map((date, di) => {
                const isCurrentMonth = date.getMonth() === month;
                const isToday = date.toDateString() === today.toDateString();
                const dayEvents = eventsForDay(date);
                const isWeekend = di >= 5;
                return (
                  <div key={di}
                    onClick={() => openCreate(date)}
                    style={{
                      minHeight:90, padding:'6px 8px', cursor:'pointer', overflow:'hidden', minWidth:0,
                      borderRight: di < 6 ? '1px solid #F3F4F6' : 'none',
                      borderBottom: wi < 5 ? '1px solid #F3F4F6' : 'none',
                      background: isWeekend && isCurrentMonth ? '#FAFAFA' : '#fff',
                      opacity: isCurrentMonth ? 1 : .35,
                      transition:'background .1s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F0F7FF'}
                    onMouseLeave={e => e.currentTarget.style.background = isWeekend && isCurrentMonth ? '#FAFAFA' : '#fff'}
                  >
                    <div style={{
                      display:'inline-flex', alignItems:'center', justifyContent:'center',
                      width:24, height:24, borderRadius:'50%', fontSize:12, fontWeight: isToday ? 700 : 400,
                      marginBottom:4,
                      background: isToday ? '#0085CA' : 'transparent',
                      color: isToday ? '#fff' : isCurrentMonth ? '#111827' : '#9CA3AF',
                    }}>{date.getDate()}</div>
                    <div style={{display:'flex',flexDirection:'column',gap:2,minWidth:0}}>
                      {dayEvents.slice(0,3).map(ev => (
                        <div key={ev.id}
                          onClick={e => openEdit(ev, e)}
                          style={{
                            padding:'1px 6px', borderRadius:4, fontSize:11, fontWeight:500,
                            background: ev.color + '22', color: ev.color,
                            whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis',
                            cursor:'pointer', lineHeight:'18px', minWidth:0,
                          }}>
                          {ev.title}
                        </div>
                      ))}
                      {dayEvents.length > 3 && (
                        <div style={{fontSize:10,color:'#9CA3AF',paddingLeft:6}}>+{dayEvents.length-3} más</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Modal */}
      {modal && (
        <div style={{position:'fixed',inset:0,zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',
          background:'rgba(15,30,60,.45)'}}
          onClick={e => { if(e.target===e.currentTarget) closeModal(); }}>
          <div style={{background:'#fff',borderRadius:12,width:440,maxWidth:'92vw',
            boxShadow:'0 8px 40px rgba(0,0,0,.14)',overflow:'hidden'}}>

            {/* Header */}
            <div style={{padding:'16px 20px 12px',borderBottom:'1px solid #F3F4F6',
              display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <div style={{fontSize:15,fontWeight:700,color:'#111827'}}>
                {modal.mode === 'create' ? 'Nuevo evento' : 'Editar evento'}
              </div>
              <button onClick={closeModal}
                style={{width:28,height:28,borderRadius:6,border:'1px solid #E5E7EB',
                  background:'#fff',cursor:'pointer',fontSize:14,color:'#6B7280',
                  display:'flex',alignItems:'center',justifyContent:'center'}}>✕</button>
            </div>

            {/* Body */}
            <div style={{padding:'16px 20px',display:'flex',flexDirection:'column',gap:12}}>

              {/* Título */}
              <div>
                <div style={{fontSize:10,fontWeight:700,letterSpacing:.8,textTransform:'uppercase',color:'#6B7280',marginBottom:5}}>
                  Título <span style={{color:'#E53935'}}>*</span>
                </div>
                <input style={inputStyle} placeholder="Nombre del evento"
                  value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} autoFocus />
              </div>

              {/* Fecha */}
              <div>
                <div style={{fontSize:10,fontWeight:700,letterSpacing:.8,textTransform:'uppercase',color:'#6B7280',marginBottom:5}}>
                  Fecha <span style={{color:'#E53935'}}>*</span>
                </div>
                <input type="date" style={inputStyle}
                  value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))} />
              </div>

              {/* Hora inicio / fin */}
              {!form.all_day && (
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                  <div>
                    <div style={{fontSize:10,fontWeight:700,letterSpacing:.8,textTransform:'uppercase',color:'#6B7280',marginBottom:5}}>Hora inicio</div>
                    <input type="time" style={inputStyle}
                      value={form.startTime} onChange={e => setForm(f => ({...f, startTime: e.target.value}))} />
                  </div>
                  <div>
                    <div style={{fontSize:10,fontWeight:700,letterSpacing:.8,textTransform:'uppercase',color:'#6B7280',marginBottom:5}}>Hora fin</div>
                    <input type="time" style={inputStyle}
                      value={form.endTime} onChange={e => setForm(f => ({...f, endTime: e.target.value}))} />
                  </div>
                </div>
              )}

              {/* Todo el día */}
              <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:13,color:'#374151'}}>
                <input type="checkbox" checked={form.all_day}
                  onChange={e => setForm(f => ({...f, all_day: e.target.checked}))} />
                Todo el día
              </label>

              {/* Descripción */}
              <div>
                <div style={{fontSize:10,fontWeight:700,letterSpacing:.8,textTransform:'uppercase',color:'#6B7280',marginBottom:5}}>Descripción</div>
                <textarea style={{...inputStyle, height:72, padding:'8px 10px', resize:'none', lineHeight:1.5}}
                  placeholder="Detalles del evento…"
                  value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} />
              </div>

              {/* Color */}
              <div>
                <div style={{fontSize:10,fontWeight:700,letterSpacing:.8,textTransform:'uppercase',color:'#6B7280',marginBottom:8}}>Color</div>
                <div style={{display:'flex',gap:8}}>
                  {COLORS.map(c => (
                    <button key={c.value} onClick={() => setForm(f => ({...f, color: c.value}))}
                      title={c.label}
                      style={{width:24,height:24,borderRadius:'50%',background:c.value,border:'none',cursor:'pointer',
                        outline: form.color === c.value ? `3px solid ${c.value}` : '3px solid transparent',
                        outlineOffset:2, transition:'outline .12s'}} />
                  ))}
                </div>
              </div>

              {/* Ticket vinculado */}
              {modal?.event?.ticket_ref && (
                <div style={{padding:'10px 12px',background:'#E8F0FE',borderRadius:8,display:'flex',flexDirection:'column',gap:6}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8}}>
                    <span style={{fontSize:11,fontWeight:700,color:'#1558D6',fontFamily:'monospace'}}>
                      {modal.event.ticket_ref}
                    </span>
                    <a href={`/backdesk/tickets/${modal.event.ticket_id}`}
                      style={{fontSize:11,fontWeight:600,color:'#1558D6',textDecoration:'none',
                        padding:'2px 8px',borderRadius:6,border:'1px solid #BFDBFE',background:'#fff',
                        cursor:'pointer'}}
                      onClick={e => { e.stopPropagation(); }}>
                      Ver ticket →
                    </a>
                  </div>
                  <div style={{fontSize:12,color:'#374151',fontWeight:500}}>{modal.event.ticket_title}</div>
                  {modal.event.ticket_assignee_name && (
                    <div style={{display:'flex',alignItems:'center',gap:6,fontSize:11,color:'#6B7280'}}>
                      <span style={{width:20,height:20,borderRadius:'50%',background:'#BFDBFE',
                        display:'inline-flex',alignItems:'center',justifyContent:'center',
                        fontSize:10,fontWeight:700,color:'#1558D6',flexShrink:0}}>
                        {modal.event.ticket_assignee_name.charAt(0).toUpperCase()}
                      </span>
                      Asignado a <strong style={{color:'#374151'}}>{modal.event.ticket_assignee_name}</strong>
                    </div>
                  )}
                  {!modal.event.ticket_assignee_name && (
                    <div style={{fontSize:11,color:'#9CA3AF'}}>Sin asignar</div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{padding:'10px 20px 16px',display:'flex',justifyContent:'space-between',alignItems:'center',gap:8}}>
              <div>
                {modal.mode === 'edit' && (
                  <button onClick={handleDelete} disabled={saving}
                    style={{height:34,padding:'0 14px',borderRadius:8,fontSize:12,fontWeight:600,cursor:'pointer',
                      background: delConfirm ? '#FEF2F2' : '#fff',
                      color: delConfirm ? '#B91C1C' : '#6B7280',
                      border: '1.5px solid ' + (delConfirm ? '#FECACA' : '#E5E7EB')}}>
                    {delConfirm ? '¿Confirmar borrado?' : 'Eliminar'}
                  </button>
                )}
              </div>
              <div style={{display:'flex',gap:8}}>
                <button onClick={closeModal}
                  style={{height:34,padding:'0 16px',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer',
                    background:'#fff',color:'#6B7280',border:'1.5px solid #E5E7EB'}}>
                  Cancelar
                </button>
                <button onClick={handleSave} disabled={!valid || saving}
                  style={{height:34,padding:'0 18px',borderRadius:8,fontSize:13,fontWeight:700,
                    cursor: !valid ? 'not-allowed' : 'pointer',
                    background: !valid ? '#D1D5DB' : '#0085CA',
                    color:'#fff', border:'none',
                    boxShadow: valid ? '0 2px 6px #0085CA44' : 'none'}}>
                  {modal.mode === 'create' ? 'Crear evento' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </BackDeskLayout>
  );
}
